import { defaultCurrency } from "@/lib/currency";
import { formatCurrency, formatPercentage, toPercentFraction } from "@/utils/formatEuro";

import type { MarketCar } from "@/types/market";

const SEARCHABLE_FIELDS = [
  "model_name",
  "make_name",
  "produced_cars",
  "production_years",
  "current_valuation",
  "year_over_year_gain_loss",
  "variant",
  "category",
  "coachbuilder",
  "description",
  "value_adjustment",
] as const satisfies readonly (keyof MarketCar)[];

export function normalizeMarketCarSearchValue(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/[,%+]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getMarketCarSearchableValues(
  row: MarketCar,
  currencyCode: string = defaultCurrency.code
): string[] {
  const values: string[] = [`${row.make_name} ${row.model_name}`];

  for (const field of SEARCHABLE_FIELDS) {
    const value = row[field];
    if (value === undefined || value === null || value === "") continue;

    if (typeof value === "number") {
      values.push(String(value));
      if (field === "produced_cars") {
        values.push(value.toLocaleString());
      }
      if (field === "current_valuation") {
        values.push(formatCurrency(value, currencyCode));
      }
      if (field === "year_over_year_gain_loss") {
        const formatted = formatPercentage(toPercentFraction(value), undefined, 2);
        values.push(formatted, value > 0 ? `+${formatted}` : formatted);
      }
      continue;
    }

    values.push(String(value));
  }

  return values;
}

export function marketCarMatchesSearch(
  row: MarketCar,
  query: string,
  currencyCode?: string
): boolean {
  const q = normalizeMarketCarSearchValue(query);
  if (!q) return true;

  return getMarketCarSearchableValues(row, currencyCode).some((value) =>
    normalizeMarketCarSearchValue(value).includes(q)
  );
}

export function filterMarketCarsBySearch(
  rows: MarketCar[],
  query: string,
  currencyCode?: string
): MarketCar[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return rows;

  return rows.filter((row) => marketCarMatchesSearch(row, trimmedQuery, currencyCode));
}
